import { LOCAL } from "@mn/shared";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type * as Y from "yjs";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useColumnChildren } from "../hooks";
import { useBoardUI } from "../store";
import { CardView } from "./CardView";

const GAP = 10;

/** Column: children are cards whose `parent` is this column, ordered by their fractional `order` key, stacked by measured height. */
export function ColumnBody({
  id,
  ymap,
  title,
  collapsed,
}: {
  id: string;
  ymap: Y.Map<unknown>;
  title: string;
  collapsed?: boolean;
}) {
  const { handle, canEdit } = useBoard();
  const t = useT();
  const cards = handle.types.cards;
  const dropIndex = useBoardUI((s) => (s.columnDrop?.columnId === id ? s.columnDrop.index : null));
  const allChildren = useColumnChildren(cards, id);
  const dragging = useBoardUI((s) => s.dragging);
  const children = dragging ? allChildren.filter((cid) => !dragging.has(cid)) : allChildren;
  const [heights, setHeights] = useState<Record<string, number>>({});
  const onHeight = useCallback(
    (cid: string, h: number) => setHeights((prev) => (prev[cid] === h ? prev : { ...prev, [cid]: h })),
    [],
  );
  const tx = (fn: () => void) => handle.doc.transact(fn, LOCAL);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // expose the column's content box so drops can compute insertion indexes
    if (ref.current) ref.current.dataset.columnChildren = children.join(",");
  });

  let y = 0;
  const offsets: Record<string, number> = {};
  const PLACEHOLDER_H = 60;
  children.forEach((cid, i) => {
    if (dropIndex === i) y += PLACEHOLDER_H + GAP;
    offsets[cid] = y;
    y += (heights[cid] ?? 80) + GAP;
  });
  if (dropIndex != null && dropIndex >= children.length) y += PLACEHOLDER_H + GAP;
  const contentH = collapsed ? 0 : Math.max(y - GAP, 24);

  return (
    <div ref={ref} data-column={id} className="flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <input
          value={title}
          readOnly={!canEdit}
          placeholder={t.columnTitle}
          onChange={(e) => tx(() => ymap.set("title", e.target.value))}
          onPointerDown={(e) => e.stopPropagation()}
          className="min-w-0 flex-1 bg-transparent text-[14px] font-semibold outline-none placeholder:text-dim"
        />
        <span className="text-[11px] text-dim">{children.length}</span>
        <button
          type="button"
          onClick={() => tx(() => ymap.set("collapsed", !collapsed))}
          onPointerDown={(e) => e.stopPropagation()}
          className="text-dim hover:text-text"
          aria-label={collapsed ? "+" : "−"}
        >
          {collapsed ? "+" : "−"}
        </button>
      </div>
      <div className="relative mx-2 mb-2" style={{ height: contentH, transition: "height 150ms ease" }}>
        {!collapsed &&
          children.map((cid) => (
            <div
              key={cid}
              className="absolute left-0 right-0 top-0"
              style={{ transform: `translateY(${offsets[cid]}px)`, transition: "transform 150ms ease" }}
            >
              {cards.get(cid) ? (
                <CardView id={cid} ymap={cards.get(cid)!} inColumn onHeight={onHeight} />
              ) : null}
            </div>
          ))}
        {!collapsed && dropIndex != null ? (
          <div
            className="absolute left-0 right-0 rounded-[2px] border-2 border-dashed border-[#8aa9ff]/60"
            style={{
              top:
                dropIndex >= children.length
                  ? contentH - PLACEHOLDER_H
                  : (offsets[children[dropIndex]!] ?? 0) - PLACEHOLDER_H - GAP,
              height: PLACEHOLDER_H,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
